import React, { useState } from 'react';

// Attempt to parse ai_feedback as structured CoachingOutput JSON.
// Falls back to null if it's old-style markdown.
function parseCoachingOutput(raw) {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.fixes) && Array.isArray(parsed.strengths)) {
      return parsed;
    }
  } catch (_) {}
  return null;
}

// A+ severity edge — monochrome, darker = higher priority (Spectrum §6)
const TIER_EDGE = {
  1: 'var(--tier-edge-1)',
  2: 'var(--tier-edge-2)',
  3: 'var(--tier-edge-3)',
};

// Thin left-bar section header
function SectionLabel({ children, accent = 'var(--text-faint)' }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <div
        className="w-0.5 h-3 rounded-full flex-shrink-0"
        style={{ background: `linear-gradient(180deg, ${accent} 0%, rgba(0,0,0,0) 100%)` }}
      />
      <h3 className="font-mono text-[10px] font-medium uppercase tracking-[0.2em]"
          style={{ color: 'var(--text-faint)' }}>
        {children}
      </h3>
    </div>
  );
}

// Before/After fix card — neutral chrome + monochrome severity edge (Spectrum A+, §6).
// `priority` (1 highest → 3 lowest) is derived from fix order by the caller.
function FixCard({ fix, priority }) {
  const edge = TIER_EDGE[priority] || TIER_EDGE[3];
  return (
    <div
      className="rounded-xl p-4 space-y-3"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderLeft: `3px solid ${edge}`,
      }}
    >
      {/* Ordinal tier chip + what happened */}
      <div className="flex items-start gap-2.5">
        <span
          className="font-mono text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5"
          style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}
        >
          {priority} · {fix.tier}
        </span>
        <p className="text-sm leading-snug" style={{ color: 'var(--text-primary)' }}>
          {fix.what_happened}
        </p>
      </div>

      {/* Before → After comparison (status colour — danger-dim → success-dim — is allowed inside a fix) */}
      {(fix.before_text || fix.after_text) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {fix.before_text && (
            <div
              className="rounded-lg p-3"
              style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}
            >
              <p className="font-mono text-[9px] uppercase tracking-widest mb-1.5"
                 style={{ color: 'var(--color-danger)' }}>Before</p>
              <p className="text-xs leading-relaxed italic" style={{ color: 'var(--text-muted)' }}>
                "{fix.before_text}"
              </p>
            </div>
          )}
          <div
            className="rounded-lg p-3"
            style={{ background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)' }}
          >
            <p className="font-mono text-[9px] uppercase tracking-widest mb-1.5"
               style={{ color: 'var(--color-success)' }}>After</p>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              "{fix.after_text}"
            </p>
          </div>
        </div>
      )}

      {/* Fix instruction */}
      {fix.fix_instruction && (
        <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Rule: </span>{fix.fix_instruction}
        </p>
      )}
    </div>
  );
}

// Behavioral drill card
function DrillCard({ drill }) {
  if (!drill?.action) return null;
  return (
    <div
      className="rounded-xl p-4"
      style={{ background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)' }}
    >
      <SectionLabel accent="var(--color-info)">Practice drill</SectionLabel>
      <p className="text-xs mb-1" style={{ color: 'var(--text-faint)' }}>
        When: {drill.trigger}
      </p>
      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{drill.action}</p>
    </div>
  );
}

// 2-3 key metric pills
function MetricPills({ metrics }) {
  if (!metrics?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {metrics.map((m, i) => (
        <div
          key={i}
          className="rounded-lg px-3 py-2 flex-shrink-0"
          style={{ background: 'var(--accent-dim)', border: '1px solid var(--accent-glow)' }}
        >
          <p className="font-mono text-[9px] uppercase tracking-widest"
             style={{ color: 'var(--text-faint)' }}>{m.label}</p>
          <p className="font-mono text-sm font-semibold mt-0.5" style={{ color: 'var(--accent)' }}>{m.value}</p>
          <p className="text-[11px] leading-snug mt-0.5" style={{ color: 'var(--text-faint)' }}>{m.context}</p>
        </div>
      ))}
    </div>
  );
}

// Collapsed suppressed issues accordion
function SuppressedLog({ items }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <div>
      <button
        onClick={() => setOpen(v => !v)}
        className="text-xs flex items-center gap-1.5"
        style={{ color: 'var(--text-faint)' }}
      >
        <span style={{ transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform 0.15s' }}>
          ▶
        </span>
        {items.length} saved for next session
      </button>
      {open && (
        <ul className="mt-2 space-y-1">
          {items.map((item, i) => (
            <li
              key={i}
              className="text-xs pl-3 border-l"
              style={{ color: 'var(--text-faint)', borderColor: 'var(--border-subtle)' }}
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Structured coaching report (new JSON format)
function StructuredCoachingReport({ output }) {
  return (
    <div className="space-y-4">
      {/* Summary banner */}
      {output.summary_banner && (
        <div
          className="rounded-xl px-4 py-3"
          style={{ background: 'var(--accent-dim)', border: '1px solid var(--accent-glow)' }}
        >
          <p className="text-sm font-medium" style={{ color: 'var(--accent)' }}>{output.summary_banner}</p>
        </div>
      )}

      {/* Strengths */}
      {output.strengths?.length > 0 && (
        <div>
          <SectionLabel accent="var(--color-success)">What worked</SectionLabel>
          <ul className="space-y-1.5">
            {output.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--color-success)', flexShrink: 0 }}>✓</span>
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Priority fixes */}
      {output.fixes?.length > 0 && (
        <div>
          <SectionLabel>Priority fixes</SectionLabel>
          <div className="space-y-3">
            {output.fixes.map((fix, i) => <FixCard key={i} fix={fix} priority={Math.min(i + 1, 3)} />)}
          </div>
        </div>
      )}

      {/* Key metrics */}
      {output.key_metrics?.length > 0 && (
        <div>
          <SectionLabel>Key numbers</SectionLabel>
          <MetricPills metrics={output.key_metrics} />
        </div>
      )}

      {/* Drill */}
      <DrillCard drill={output.behavioral_drill} />

      {/* Suppressed log */}
      <SuppressedLog items={output.suppressed_log} />
    </div>
  );
}

/**
 * CoachingReport
 *
 * Renders ai_feedback as structured cards (new JSON format) or as plain text
 * (backward-compat for speeches with old markdown feedback).
 */
export default function CoachingReport({ aiFeedback }) {
  if (!aiFeedback) return null;

  const structured = parseCoachingOutput(aiFeedback);

  if (structured) {
    return <StructuredCoachingReport output={structured} />;
  }

  // Fallback: old markdown feedback — render with lightweight inline parser
  return <LegacyMarkdown text={aiFeedback} />;
}

// Lightweight Markdown renderer for legacy ai_feedback strings.
// Handles: ## headings, ### subheadings, **bold**, - list items, blank-line paragraphs.
// No external deps — keeps bundle small and avoids XSS (no dangerouslySetInnerHTML).
function LegacyMarkdown({ text }) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let listItems = [];

  const flushList = (key) => {
    if (!listItems.length) return;
    elements.push(
      <ul key={`ul-${key}`} className="space-y-1 pl-4 my-2">
        {listItems.map((item, i) => (
          <li key={i} className="text-sm flex gap-2 leading-snug" style={{ color: 'var(--text-secondary)' }}>
            <span style={{ color: 'var(--accent)', flexShrink: 0 }}>·</span>
            <span>{renderInline(item)}</span>
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  const renderInline = (str) => {
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i} style={{ color: 'var(--text-primary)' }}>{part.slice(2, -2)}</strong>
        : part
    );
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('## ')) {
      flushList(idx);
      elements.push(
        <h3 key={idx} className="font-bold text-sm mt-4 mb-1" style={{ color: 'var(--text-primary)' }}>
          {trimmed.slice(3)}
        </h3>
      );
    } else if (trimmed.startsWith('### ')) {
      flushList(idx);
      elements.push(
        <h4 key={idx} className="font-semibold text-xs uppercase tracking-wide mt-3 mb-1" style={{ color: 'var(--text-faint)' }}>
          {trimmed.slice(4)}
        </h4>
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listItems.push(trimmed.slice(2));
    } else if (trimmed === '') {
      flushList(idx);
    } else {
      flushList(idx);
      elements.push(
        <p key={idx} className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          {renderInline(trimmed)}
        </p>
      );
    }
  });
  flushList('end');

  return (
    <div
      className="rounded-xl p-4 space-y-1"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
    >
      {elements}
    </div>
  );
}
